const defaultColors = {
    white: '#ffffff',
    whiteSmoke: '#f1f1f1',
    whiteFog: '#f9f9f9',
    whiteGrey: '#d8d8d8',
    black: '#353535',
    grey: '#707070',
    lightGrey: '#ccc',

    primary: '#63b8a4',
    secondary: '#ff565d',

    error: '#e32438',
    success: '#64970a',
    inactive: '#c6c6c6',
    billPrimary: '#077bc2'
};

const defaultScreenSizes = {
    xTiny: '340px',
    tiny: '380px',
    mobile: '768px',
    medium: '769px',
    large: '1024px'
};

const defaultFonts = {
    // tiny font sizes (mobile first)
    baseFontSize: '1.4rem', // ex. 1.4rem would be "14px" equivalent
    lineHeight: '1.4',
    h1Size: '2.8rem', // equiv "28px"
    h2Size: '2.4rem', // equiv "24px"
    h3Size: '2rem', // equiv "20px"
    h4Size: '1.8rem', // equiv "18px"
    h5Size: '1.6rem', // equiv "16px"
    h6Size: '1.4rem', // equiv "14px"

    // large font sizes
    baseFontSizeL: '1.4rem', // ex. 1.6rem would be "16px" equivalent
    lineHeightL: '1.5',
    h1SizeL: '3.2rem', // equiv "32px"
    h2SizeL: '2.8rem', // equiv "28px"
    h3SizeL: '2.4rem', // equiv "24px"
    h4SizeL: '2rem', // equiv "20px"
    h5SizeL: '1.8rem', // equiv "18px"
    h6SizeL: '1.6rem', // equiv "16px"

    boldWeight: 700,
    regularWeight: 400
};

export const generateCustomizedTheme = ({
    colors: colorsOverride,
    screenSizes: screenSizeOverrides,
    fonts: fontsOverrides,
    overrideFunction
}) => {
    const colors = { ...defaultColors, ...colorsOverride };
    const screenSizes = { ...defaultScreenSizes, ...screenSizeOverrides };
    const fonts = { ...defaultFonts, ...fontsOverrides };
    const overrides = overrideFunction({ colors, screenSizes, fonts });

    return {
        ...colors,
        ...screenSizes,
        ...fonts,
        // font stacks
        // fonts are declared in index.html for Google fonts or with @font-face in index.scss for custom fonts
        // custom fonts are put in public/fonts
        fontStackCommon: "'Arial', sans-serif", // system font stack
        fontStackHeadings: "'Arial', sans-serif", // headings font
        fontStackMonospace: "'Arial', sans-serif", // monospace font
        fontStackSpecial: "'Arial', sans-serif", // special font

        // title colors
        h1Color: colors.black,
        h1BorderColor: colors.secondary,
        h2Color: colors.black,
        h2BorderColor: colors.secondary,
        h3Color: colors.black,
        hXColor: colors.black,

        // link colors
        linkColor: colors.primary,
        linkHoverColor: colors.primary,

        // button's colors in project
        buttonFontStyle: 'uppercase',
        buttonFontSize: '14px',
        buttonBorderRadius: '23px',
        buttonFontWright: fonts.boldWeight,
        // main button
        buttonMainBg: colors.primary,
        buttonMainText: colors.white,
        buttonMainBorder: colors.primary,
        // secondary button
        buttonSecondBg: colors.secondary,
        buttonSecondText: colors.white,
        buttonSecondBorder: colors.secondary,
        // third button type
        buttonThirdBg: colors.white,
        buttonThirdText: colors.black,
        buttonThirdBorder: colors.lightGrey,
        // fourth button type
        buttonFourthBg: colors.white,
        buttonFourthText: colors.primary,
        buttonFourthBorder: colors.primary,
        // fifth button type
        buttonFifthBg: colors.black,
        buttonFifthText: colors.white,
        buttonFifthBorder: colors.black,
        // sixth button type
        buttonSixthBg: colors.primary,
        buttonSixthText: colors.white,
        buttonSixthBorder: colors.primary,
        // close button
        closeButtonColor: colors.lightGrey,

        // common
        hrBackgroundColor: colors.secondary,

        // header
        searchIcon: colors.black,
        accountIcon: colors.black,
        navMobIcon: colors.black,
        memberNavTextWeight: fonts.boldWeight,
        loginTextColor: colors.black,
        registerTextColor: colors.secondary,
        accountTextColor: colors.black,
        logoutTextColor: colors.black,

        // breadcrumb
        breadcrumbColor: colors.grey,

        // nav
        navItemTransform: 'none',
        navItemFontWeight: fonts.boldWeight,
        navItemText: colors.black,
        navItemBorderHover: colors.black,
        navItemActiveText: colors.primary,
        navItemActiveTextWeight: fonts.boldWeight,
        navItemActiveBorder: colors.primary,
        navBackgroundColor: colors.white,
        navBorderColor: colors.whiteSmoke,
        navMobileBorderColor: colors.grey,

        // sub nav 'Nos catégories'
        subNavBg: colors.primary,
        subNavText: colors.white,
        subNavTextWeight: fonts.boldWeight,
        subNavTextHover: colors.grey,
        subNavTextHoverWeight: fonts.boldWeight,
        subNavSvgColor: colors.white,
        subNavMobileBackgroundColor: colors.primary,

        // home
        carouselBackgroundColor: colors.whiteSmoke,
        carouselArrowColor: colors.lightGrey,
        carouselMobileArrowColor: colors.white,

        // search
        searchInputTextColor: colors.black,
        searchResultBackgroundColor: colors.white,
        searchResultBorderColor: colors.lightGrey,
        searchResultTextColor: colors.black,

        // interstitiel background color
        interstitielStatusBg: colors.primary,
        interstitielStatusText: colors.white,

        // partner block colors
        partnerOfferMain: colors.secondary,
        partnerOfferMainValueWeight: fonts.boldWeight,
        partnerOfferMainTextWeight: fonts.boldWeight,
        partnerOfferSecond: colors.grey,
        partnerOfferSecondWeight: fonts.regularWeight,
        partnerBlockBorderColor: colors.lightGrey,
        partnerBlockHorizontalBorderColor: colors.lightGrey,
        partnerButtonBgColor: colors.primary,
        partnerButtonColor: colors.white,
        partnerButtonBorderColor: colors.primary,

        // form
        checkboxRadioBackgroundColor: colors.white,
        checkboxRadioBorderWhite: colors.white,
        checkboxRadioBorderColor: colors.lightGrey,
        inputBorderColor: colors.lightGrey,
        fileBackgroundColor: colors.whiteSmoke,
        selectDropdownArrowColor: colors.secondary,

        // account
        accountValidCashbackBlockColor: colors.primary,
        accountValidCashbackTextColor: colors.white,
        accountSvgCrossColor: colors.grey,
        accountSvgCircleColor: colors.whiteSmoke,
        accountWaitingCashbackBlockColor: colors.secondary,
        accountWaitingCashbackTextColor: colors.white,
        accountTotalCashbackTextColor: colors.black,
        accountCashbackTableBorderColor: colors.lightGrey,
        accountCashbackTableTheadBackgroundColor: colors.black,
        accountCashbackTableGainColor: colors.black,
        accountCashbackHistoryLegendStateColor: colors.black,
        accountPaymentRequestInfoSvgTextColor: colors.primary,
        accountPaymentRequestInfoSvgCircleColor: colors.white,
        accountCashbackLegendLineColor: colors.lightGrey,

        accountMessagesTableColor: colors.whiteSmoke,
        accountMessageFieldColor: colors.black,
        accountMessagesBorderColor: colors.grey,

        // messages
        msgSuccessColor: colors.primary,

        // modal
        modalBlockerBackground: colors.black,
        modalBackgroundColor: colors.white,

        // merchant card svg color
        partnerCardDiscountIcon: colors.primary,
        partnerCardMainDiscountColor: colors.secondary,
        partnerCardValidationDateColor: colors.primary,
        partnerCardDiscountListDiscountColor: colors.black,
        partnerCardDiscountListTextColor: colors.black,
        merchantCardMeaBackgroundColor: colors.whiteSmoke,
        merchantCardCouponBorderColor: colors.lightGrey,
        partnerCardEbonAvailabilityDateColor: colors.secondary,
        partnerCardEbonValidateCartBackgroundColor: colors.primary,
        partnerCardEbonValidateCartTextColor: colors.white,
        partnerCardEbonValidateCartBorderColor: colors.primary,

        // maps
        fullscreenButtonTextColor: colors.primary,
        discountBackgroundColor: colors.secondary,
        discountTextColor: colors.white,
        contentColor: colors.black,
        distanceColor: colors.black,

        // coupon offer
        couponSvgColor: colors.primary,

        // interstitiel
        interstitielCopyPasteSvgColor: colors.primary,

        // how it work
        howItWorkBlockBoldColor: colors.secondary,
        howItWorkShadowBackground: colors.white,
        howItWorkAfterColor: colors.lightGrey,
        faqContainerColor: colors.whiteSmoke,
        faqContainerTitleColor: colors.black,

        // customer service
        customerServiceBlockBackground: colors.whiteSmoke,
        customerServiceAlertMessageBackground: colors.whiteSmoke,

        // directory
        directoryPartnersCountTextColor: colors.secondary,
        directoryFilterContainerColor: colors.white,
        categoryHeaderTextColor: colors.white,
        categoryHeaderMobileTextColor: colors.black,

        // faq
        arrowSvgColor: colors.lightGrey,
        faqLinkTextColor: colors.black,
        faqBorderColor: colors.lightGrey,

        // cookie banner
        cookieBannerBackgroundColor: colors.grey,
        cookieBannerTextColor: colors.white,
        cookieBannerButtonColor: colors.primary,

        // toolbar
        toolbarBorderColor: colors.lightGrey,
        toolbarInstallationTextColor: colors.white,
        toolbarInstallationBlockColor: colors.primary,

        // footer
        footerTextColor: colors.white,
        footerBackgroundColor: colors.black,

        // colors to override in knacss - /!\ DO NOT DELETE THIS PART /!\
        brandPrimary: colors.primary,
        brandSecondary: colors.black,
        colorBase: colors.black,
        colorLink: colors.linkColor,
        colorLinkHover: colors.linkHoverColor,
        colorText: colors.black,
        backgroundBase: colors.white,
        borderColor: colors.lightGrey,
        errorColor: colors.error,

        // spacings (choose unit you prefer)
        tinyValue: '0.5rem', // tiny value for margins / paddings
        tinyPlusValue: '0.7rem', // tiny+ value for margins / paddings
        smallValue: '1rem', // small value for margins / paddings
        smallPlusValue: '1.5rem', // small+ value for margins / paddings
        mediumValue: '2rem', // medium value for margins / paddings
        mediumPlusValue: '3rem', // medium+ value for margins / paddings
        largeValue: '4rem', // large value for margins / paddings
        largePlusValue: '6rem', // large value for margins / paddings
        extraLargeValue: '8rem', // extra large value for margins / paddings
        extraLargePlusValue: '12rem', // extra large value for margins / paddings
        ultraLargeValue: '16rem', // ultra large value for margins / paddings
        ultraLargePlusValue: '20rem', // ultra large value for margins / paddings

        gridGutter: '15px',
        gridGutterLarge: '30px',
        gridGutterExtraLarge: '60px',

        borderRadiusWidth: '3px',
        ...overrides
    };
};



// WEBPACK FOOTER //
// ./src/style/baseTheme.js